import { api, throwApiError } from "../../../api/api";
import type { DpEmployeeStatus, DpPaginated } from "./dpTypes";

export type DpLeaveType =
  | "ATESTADO_MEDICO"
  | "AUXILIO_DOENCA_INSS"
  | "ACIDENTE_TRABALHO"
  | "LICENCA_MATERNIDADE"
  | "LICENCA_PATERNIDADE"
  | "SUSPENSAO_DISCIPLINAR"
  | "LICENCA_NAO_REMUNERADA"
  | "SERVICO_MILITAR"
  | "FALTA_JUSTIFICADA"
  | "OUTRO";

export const LEAVE_TYPE_LABELS: Record<DpLeaveType, string> = {
  ATESTADO_MEDICO: "Atestado médico",
  AUXILIO_DOENCA_INSS: "Auxílio-doença (INSS)",
  ACIDENTE_TRABALHO: "Acidente de trabalho",
  LICENCA_MATERNIDADE: "Licença-maternidade",
  LICENCA_PATERNIDADE: "Licença-paternidade",
  SUSPENSAO_DISCIPLINAR: "Suspensão disciplinar",
  LICENCA_NAO_REMUNERADA: "Licença não remunerada",
  SERVICO_MILITAR: "Serviço militar",
  FALTA_JUSTIFICADA: "Falta justificada",
  OUTRO: "Outro",
};

/** Ajuda curta exibida no formulário ao escolher o tipo. */
export const LEAVE_TYPE_HINTS: Record<DpLeaveType, string> = {
  ATESTADO_MEDICO: "A empresa paga os primeiros 15 dias; a partir do 16º o sistema cria a continuação do INSS.",
  AUXILIO_DOENCA_INSS: "Benefício já pago pelo INSS (ex.: colaborador que já estava afastado).",
  ACIDENTE_TRABALHO: "15 dias da empresa e o restante do INSS. Registre a CAT (até o 1º dia útil seguinte).",
  LICENCA_MATERNIDADE: "120 dias quando o término fica vazio. Para o movimento, os dias são do INSS.",
  LICENCA_PATERNIDADE: "5 dias quando o término fica vazio. Empresa Cidadã: informe 20 dias.",
  SUSPENSAO_DISCIPLINAR: "No máximo 30 dias (art. 474 CLT). Sem remuneração.",
  LICENCA_NAO_REMUNERADA: "Sem remuneração. Pode ficar em aberto.",
  SERVICO_MILITAR: "Sem remuneração. Pode ficar em aberto.",
  FALTA_JUSTIFICADA: "Óbito, casamento, doação de sangue etc. (art. 473). Não muda o status para Afastado.",
  OUTRO: "Escolha quem paga os dias.",
};

export type DpLeavePayer = "EMPREGADOR" | "INSS" | "NENHUM";

export const LEAVE_PAYER_LABELS: Record<DpLeavePayer, string> = {
  EMPREGADOR: "Empresa",
  INSS: "INSS",
  NENHUM: "Sem remuneração",
};

export type DpLeaveStatus = "ATIVO" | "ENCERRADO" | "CANCELADO";

export const LEAVE_STATUS_LABELS: Record<DpLeaveStatus, string> = {
  ATIVO: "Ativo",
  ENCERRADO: "Encerrado",
  CANCELADO: "Cancelado",
};

export const LEAVE_STATUS_BADGE: Record<DpLeaveStatus, string> = {
  ATIVO: "dpBadge--orange",
  ENCERRADO: "dpBadge--gray",
  CANCELADO: "dpBadge--gray",
};

/** Tipos que podem continuar um afastamento anterior (regra dos 15 dias). */
export const CHAINABLE_TYPES: DpLeaveType[] = ["ATESTADO_MEDICO", "ACIDENTE_TRABALHO", "AUXILIO_DOENCA_INSS"];

/** Afastamento SEM dado de saúde — é o que lista e detalhe devolvem. */
export type DpLeave = {
  id: string;
  employeeId: string;
  contractId: string;
  type: DpLeaveType;
  startDate: string;
  endDate: string | null;
  expectedEndDate: string | null;
  days: number | null;
  payer: DpLeavePayer;
  employerPaidDays: number;
  catNumber: string | null;
  catIssuedAt: string | null;
  parentLeaveId: string | null;
  isAutoContinuation: boolean;
  status: DpLeaveStatus;
  notes: string | null;
  closedAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  createdBy: { id: string; nomeCompleto: string };
  closedBy: { id: string; nomeCompleto: string } | null;
  continuations: DpLeave[];
  /** Soma do evento (origem + continuação automática do INSS). */
  eventEndDate: string | null;
  eventDays: number | null;
  inssDays: number | null;
};

export type DpLeaveListItem = DpLeave & { employee: { id: string; fullName: string; socialName: string | null } };

export type DpLeaveDetail = DpLeave & {
  employee: { id: string; fullName: string; socialName: string | null; status: DpEmployeeStatus };
  parentLeave: { id: string; type: DpLeaveType; startDate: string; endDate: string | null; status: DpLeaveStatus } | null;
};

export type DpLeaveSensitive = {
  cid: string | null;
  physicianName: string | null;
  physicianCrm: string | null;
  hasAttachment: boolean;
  attachmentFileName: string | null;
  hasCat: boolean;
  catFileName: string | null;
};

export type CreateLeavePayload = {
  type: DpLeaveType;
  startDate: string;
  endDate?: string | null;
  days?: number;
  expectedEndDate?: string | null;
  payer?: DpLeavePayer;
  parentLeaveId?: string | null;
  notes?: string | null;
  catNumber?: string | null;
  catIssuedAt?: string | null;
  // Só DP — o backend recusa (403) se vierem de outro papel.
  cid?: string | null;
  physicianName?: string | null;
  physicianCrm?: string | null;
  attachmentAssetId?: string | null;
  attachmentFileName?: string | null;
  catAssetId?: string | null;
  catFileName?: string | null;
};

export type UpdateLeavePayload = Omit<CreateLeavePayload, "type" | "startDate" | "days" | "payer" | "parentLeaveId">;

export type DpSensitiveAccessLog = {
  id: string;
  resource: "LEAVE" | "LEAVE_ATTACHMENT" | "LEAVE_CAT";
  action: "VIEW" | "DOWNLOAD";
  ip: string | null;
  userAgent: string | null;
  accessedAt: string;
  leaveId: string;
  user: { id: string; nomeCompleto: string };
  employee: { id: string; fullName: string };
  leave: { type: DpLeaveType; startDate: string };
};

export const SENSITIVE_RESOURCE_LABELS: Record<DpSensitiveAccessLog["resource"], string> = {
  LEAVE: "Dados de saúde",
  LEAVE_ATTACHMENT: "Atestado/laudo",
  LEAVE_CAT: "CAT",
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const listLeavesApi = async (
  token: string,
  params: {
    page?: number;
    limit?: number;
    employeeId?: string;
    type?: DpLeaveType;
    status?: DpLeaveStatus;
    from?: string;
    to?: string;
    search?: string;
  }
): Promise<DpPaginated<DpLeaveListItem>> => {
  try {
    const { data } = await api.get<DpPaginated<DpLeaveListItem>>("/dp/leaves", { ...auth(token), params });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar os afastamentos.");
    throw error;
  }
};

export const getLeaveApi = async (token: string, id: string): Promise<DpLeaveDetail> => {
  try {
    const { data } = await api.get<DpLeaveDetail>(`/dp/leaves/${id}`, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o afastamento.");
    throw error;
  }
};

export const getLeavesSummaryApi = async (token: string): Promise<{ active: number }> => {
  try {
    const { data } = await api.get<{ active: number }>("/dp/leaves/summary", auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao consultar os afastamentos.");
    throw error;
  }
};

/** Só DP. */
export const uploadLeaveFileApi = async (token: string, file: File): Promise<{ assetId: string; fileName: string | null }> => {
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await api.post<{ assetId: string; fileName: string | null }>("/dp/leaves/files", form, {
      headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao enviar o arquivo.");
    throw error;
  }
};

export const createLeaveApi = async (
  token: string,
  employeeId: string,
  payload: CreateLeavePayload
): Promise<{ leave: DpLeaveDetail; continuation: DpLeave | null; warnings: string[] }> => {
  try {
    const { data } = await api.post<{ leave: DpLeaveDetail; continuation: DpLeave | null; warnings: string[] }>(
      `/dp/employees/${employeeId}/leaves`,
      payload,
      auth(token)
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao lançar o afastamento.");
    throw error;
  }
};

export const updateLeaveApi = async (
  token: string,
  id: string,
  payload: UpdateLeavePayload
): Promise<{ leave: DpLeaveDetail; warnings: string[] }> => {
  try {
    const { data } = await api.patch<{ leave: DpLeaveDetail; warnings: string[] }>(`/dp/leaves/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar o afastamento.");
    throw error;
  }
};

export const closeLeaveApi = async (token: string, id: string, endDate: string) => {
  try {
    const { data } = await api.post<{ leave: DpLeaveDetail; warnings: string[] }>(`/dp/leaves/${id}/close`, { endDate }, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao encerrar o afastamento.");
    throw error;
  }
};

export const cancelLeaveApi = async (token: string, id: string, reason: string) => {
  try {
    const { data } = await api.post<DpLeaveDetail>(`/dp/leaves/${id}/cancel`, { reason }, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cancelar o afastamento.");
    throw error;
  }
};

/** Só DP; o acesso fica registrado no log. */
export const getLeaveSensitiveApi = async (token: string, id: string): Promise<DpLeaveSensitive> => {
  try {
    const { data } = await api.get<DpLeaveSensitive>(`/dp/leaves/${id}/sensitive`, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar os dados de saúde.");
    throw error;
  }
};

/** Só DP; o download fica registrado no log. */
export const getLeaveAttachmentBlobApi = async (token: string, id: string, which: "attachment" | "cat"): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(`/dp/leaves/${id}/attachment`, {
      ...auth(token),
      params: { which },
      responseType: "blob",
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao baixar o arquivo.");
    throw error;
  }
};

export const listSensitiveAccessLogsApi = async (
  token: string,
  params: { page?: number; limit?: number; employeeId?: string; leaveId?: string; from?: string; to?: string }
): Promise<DpPaginated<DpSensitiveAccessLog>> => {
  try {
    const { data } = await api.get<DpPaginated<DpSensitiveAccessLog>>("/dp/sensitive-access-logs", { ...auth(token), params });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o registro de acessos.");
    throw error;
  }
};
